/** Presentation of a tenant request (plan §10.4): the status chip, the words for who asked and
 *  who decided, and the request quote the card and both decision dialogs lead with. */
import { Box, Chip, Typography } from '@mui/material';
import type { ChipProps } from '@mui/material';

import { TENANT_REQUEST_STATUS_LABELS, isLifecycleKind } from '@/api/tenantRequests';
import type { TenantRequest, TenantRequestStatus } from '@/api/tenantRequests';
import { userLabel } from '@/features/onboarding-invites/inviteFormat';
import { instantLabel } from '@/features/tour-operators/onboard/products';

const STATUS_STYLE: Record<
  TenantRequestStatus,
  { color: ChipProps['color']; variant: ChipProps['variant'] }
> = {
  pending: { color: 'warning', variant: 'filled' },
  approved: { color: 'info', variant: 'outlined' },
  applying: { color: 'info', variant: 'filled' },
  applied: { color: 'success', variant: 'filled' },
  rejected: { color: 'error', variant: 'outlined' },
  withdrawn: { color: 'default', variant: 'outlined' },
  superseded: { color: 'default', variant: 'outlined' },
  failed: { color: 'error', variant: 'filled' },
};

const STATUS_HINT: Record<TenantRequestStatus, string> = {
  pending: 'Waiting for ops: approve (go live) or reject with a note.',
  approved: 'Approved; the go live is starting.',
  applying: 'Approved; the sample bookings are being re-synced, then the tenant goes live.',
  applied: 'The tenant went live.',
  rejected: 'Rejected with a note the client sees; it may ask again.',
  withdrawn: 'The client took it back.',
  superseded: 'The client asked again while it was pending; the newer request replaced it.',
  failed:
    'The go live failed: the tenant is still blocked. Retry with Go live on its onboarding page.',
};

/** A tenant lifecycle request's statuses (close / release / reopen, decided on the Lifecycle tab). */
const LIFECYCLE_STATUS_HINT: Record<TenantRequestStatus, string> = {
  pending: 'Waiting for an approval on the tenant’s Lifecycle tab.',
  approved: 'Approved; it is being applied.',
  applying: 'Approved; a job is applying it.',
  applied: 'Applied.',
  rejected: 'Rejected with a note; the tenant stays as it was.',
  withdrawn: 'Withdrawn; the tenant stays as it was.',
  superseded: 'Replaced by a newer request.',
  failed: 'Approved, but applying it failed — see the tenant’s Lifecycle tab.',
};

export function tenantRequestStatusHint(status: TenantRequestStatus, kind?: TenantRequest['kind']): string {
  const hints = kind && isLifecycleKind(kind) ? LIFECYCLE_STATUS_HINT : STATUS_HINT;
  return hints[status] ?? '';
}

export function TenantRequestStatusChip({ status }: { status: TenantRequestStatus }) {
  const style = STATUS_STYLE[status] ?? { color: 'default', variant: 'outlined' };
  return (
    <Chip
      size="small"
      color={style.color}
      variant={style.variant}
      label={TENANT_REQUEST_STATUS_LABELS[status] ?? status}
      sx={{ height: 22, fontSize: 12 }}
    />
  );
}

/** "Requested by ops@acme.example · Sat, 17 Oct, 18:00" (the client's dashboard user). */
export function requestedWords(r: TenantRequest, timeZone?: string | null): string {
  const when = instantLabel(r.requested_ts, timeZone) ?? r.requested_ts;
  return `Requested by ${r.requested_by ? userLabel(r.requested_by) : 'a dashboard user no longer there'} · ${when}`;
}

/** Who decided and when: "Approved by maya@… · …", "Rejected by …", or null before a decision.
 *  A request applied with no decider went live some other way (never through an approval). */
export function decidedWords(r: TenantRequest, timeZone?: string | null): string | null {
  if (!r.decided_ts) return null;
  const when = instantLabel(r.decided_ts, timeZone) ?? r.decided_ts;
  const who = r.decided_by ? ` by ${userLabel(r.decided_by)}` : '';
  switch (r.status) {
    case 'rejected':
      return `Rejected${who} · ${when}`;
    case 'withdrawn':
      return `Withdrawn by the client · ${when}`;
    case 'superseded':
      return `Replaced by a newer request · ${when}`;
    default:
      return `Approved${who} · ${when}`;
  }
}

/** The request as the client made it: who, when and the note, quoted. */
export function RequestQuote({
  request,
  timeZone,
}: {
  request: TenantRequest;
  timeZone?: string | null;
}) {
  const note = request.note?.trim();
  return (
    <Box>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {requestedWords(request, timeZone)}
      </Typography>
      {note ? (
        <Typography
          variant="body2"
          component="blockquote"
          sx={{
            m: 0,
            mt: 0.75,
            pl: 1.25,
            borderLeft: 3,
            borderColor: 'divider',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {note}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
          No note.
        </Typography>
      )}
    </Box>
  );
}
